import type { VoiceConfig } from '@kanbu/schema/contracts';
import {
  RecordCategory,
  RecordUnit,
  Gender,
  Agent,
  AgentType,
  ContentType,
  ResponseLengthPreset,
  TonePreset,
  DocumentStatus,
  FeedbackCategory,
  SourcesFormat,
  SourcesDisplayMode,
  FeedSource,
  FeedStatus,
  FeedHealth,
  ProductAvailabilityStatus,
  ShopAttributeDataType,
  ShopProductAttributeSource,
  ShopProductStatus,
  Role,
  RoleWOSuperAdmin,
  ThreadCategory,
  ChatPlacementPosition,
  ChatDisplayMode,
  UpdateFrequency,
  WebsitePageStatus,
  RemovalBehavior,
} from '@kanbu/schema/enums';
import type { SupportedLanguageCode } from '@kanbu/shared';
import { t } from '@lingui/core/macro';

type VoiceThinkingLevel = NonNullable<
  NonNullable<VoiceConfig['gemini']>['thinkingLevel']
>;
type VoiceStartOfSpeechSensitivity = NonNullable<
  NonNullable<VoiceConfig['gemini']>['startOfSpeechSensitivity']
>;
type VoiceEndOfSpeechSensitivity = NonNullable<
  NonNullable<VoiceConfig['gemini']>['endOfSpeechSensitivity']
>;

/**
 * This type contains all the translations for the enums
 * that can be reused in the admin app.
 *
 * It is used mainly to ensure type safetry on the
 * returned translations from `getEnumTranslations()`.
 */
export type EnumTranslations = {
  tonePreset: Record<TonePreset, string>;
  agentType: Record<AgentType, string>;
  agent: Record<Agent, string>;
  responseLengthPreset: Record<ResponseLengthPreset, string>;
  contentType: Record<ContentType, string>;
  supportedLanguages: Record<SupportedLanguageCode, string>;
  gender: Record<Gender, string>;
  documentStatus: Record<DocumentStatus, string>;
  feedbackCategory: Record<FeedbackCategory, string>;
  sourcesFormat: Record<SourcesFormat, string>;
  sourcesDisplayMode: Record<SourcesDisplayMode, string>;
  feedSource: Record<FeedSource, string>;
  feedStatus: Record<FeedStatus, string>;
  feedHealth: Record<FeedHealth, string>;
  shopProductStatus: Record<ShopProductStatus, string>;
  productAvailabilityStatus: Record<ProductAvailabilityStatus, string>;
  shopAttributeDataType: Record<ShopAttributeDataType, string>;
  shopProductAttributeSource: Record<ShopProductAttributeSource, string>;
  role: Record<Role, string>;
  recordUnit: Record<RecordUnit, string>;
  threadCategory: Record<ThreadCategory, string>;
  roleWOSuperAdmin: Record<RoleWOSuperAdmin, string>;
  recordCategory: Record<RecordCategory, string>;
  chatPlacementPosition: Record<ChatPlacementPosition, string>;
  chatDisplayMode: Record<ChatDisplayMode, string>;
  updateFrequency: Record<UpdateFrequency, string>;
  websitePageStatus: Record<WebsitePageStatus, string>;
  removalBehavior: Record<RemovalBehavior, string>;
  voiceThinkingLevel: Record<VoiceThinkingLevel, string>;
  voiceStartOfSpeechSensitivity: Record<VoiceStartOfSpeechSensitivity, string>;
  voiceEndOfSpeechSensitivity: Record<VoiceEndOfSpeechSensitivity, string>;
};

/**
 * This function contains translations for enums and other constants
 * that can be reused in the admin app. It is memoized to avoid
 * re-rendering the same translations.
 */
export function getEnumTranslations() {
  return {
    tonePreset: {
      [TonePreset.Friendly]: t`Friendly`,
      [TonePreset.Casual]: t`Casual`,
      [TonePreset.Balanced]: t`Balanced`,
      [TonePreset.Professional]: t`Professional`,
      [TonePreset.Formal]: t`Formal`,
    },
    threadCategory: {
      [ThreadCategory.Voice]: t`Voice`,
      [ThreadCategory.Chat]: t`Chat`,
    },
    agentType: {
      [AgentType.Chat]: t`Chat`,
      [AgentType.Voice]: t`Voice`,
    },
    chatPlacementPosition: {
      [ChatPlacementPosition.Left]: t`Left`,
      [ChatPlacementPosition.Center]: t`Center`,
      [ChatPlacementPosition.Right]: t`Right`,
    },
    chatDisplayMode: {
      [ChatDisplayMode.Widget]: t`Widget`,
      [ChatDisplayMode.Immersive]: t`Full page`,
    },
    recordUnit: {
      [RecordUnit.Tokens]: t`Tokens`,
      [RecordUnit.Characters]: t`Characters`,
      [RecordUnit.Seconds]: t`Seconds`,
      [RecordUnit.Credits]: t`Credits`,
    },
    recordCategory: {
      [RecordCategory.Agent]: t`Agent`,
      [RecordCategory.Data]: t`Data`,
      [RecordCategory.Other]: t`Other`,
      [RecordCategory.RAG]: t`RAG`,
    },
    gender: {
      [Gender.None]: t`None`,
      [Gender.Male]: t`Male`,
      [Gender.Female]: t`Female`,
    },
    feedbackCategory: {
      [FeedbackCategory.InsufficientDetail]: t`Insufficient detail`,
      [FeedbackCategory.IrrelevantInformation]: t`Irrelevant information`,
      [FeedbackCategory.MissingInformation]: t`Missing information`,
      [FeedbackCategory.IncorrectInformation]: t`Incorrect information`,
      [FeedbackCategory.Other]: t`Other`,
    },
    agent: {
      [Agent.Conversational]: t`Conversational`,
      [Agent.ShopAssistant]: t`Shop assistant`,
    },
    contentType: {
      [ContentType.ExternalDocument]: t`Online files`,
      [ContentType.Website]: t`Websites`,
      [ContentType.Document]: t`Uploaded files`,
    },
    sourcesFormat: {
      [SourcesFormat.Simple]: t`Simple`,
      [SourcesFormat.Academic]: t`Academic`,
    },
    sourcesDisplayMode: {
      [SourcesDisplayMode.Pills]: t`Pills`,
      [SourcesDisplayMode.Structured]: t`Structured`,
    },
    responseLengthPreset: {
      [ResponseLengthPreset.VeryShort]: t`Very short`,
      [ResponseLengthPreset.Short]: t`Short`,
      [ResponseLengthPreset.Medium]: t`Medium`,
      [ResponseLengthPreset.Long]: t`Long`,
      [ResponseLengthPreset.VeryLong]: t`Very long`,
    },
    supportedLanguages: {
      en: t`English`,
      de: t`German`,
      fr: t`French`,
      es: t`Spanish`,
      it: t`Italian`,
      nl: t`Dutch`,
      pl: t`Polish`,
      pt: t`Portuguese`,
      da: t`Danish`,
      fi: t`Finnish`,
      no: t`Norwegian`,
      sv: t`Swedish`,
      is: t`Icelandic`,
      ru: t`Russian`,
      uk: t`Ukrainian`,
      cs: t`Czech`,
      sk: t`Slovak`,
      hu: t`Hungarian`,
      bg: t`Bulgarian`,
      ro: t`Romanian`,
      sr: t`Serbian`,
      hr: t`Croatian`,
      mk: t`Macedonian`,
      sl: t`Slovenian`,
      sq: t`Albanian`,
      lt: t`Lithuanian`,
      lv: t`Latvian`,
      et: t`Estonian`,
      el: t`Greek`,
      tr: t`Turkish`,
      he: t`Hebrew`,
      ca: t`Catalan`,
      gd: t`Scottish Gaelic`,
      cy: t`Welsh`,
      ga: t`Irish`,
      bs: t`Bosnian`,
      be: t`Belarusian`,
      ka: t`Georgian`,
      hy: t`Armenian`,
    },
    documentStatus: {
      [DocumentStatus.ParsingError]: t`Parsing error`,
      [DocumentStatus.Uploading]: t`Uploading`,
      [DocumentStatus.Ready]: t`Ready`,
      [DocumentStatus.Parsing]: t`Parsing`,
      [DocumentStatus.ParsingAsync]: t`Parsing (async)`,
      [DocumentStatus.EmbeddingError]: t`Embedding error`,
      [DocumentStatus.Parsed]: t`Parsed`,
      [DocumentStatus.Embedding]: t`Embedding`,
      [DocumentStatus.Done]: t`Done`,
    },
    feedSource: {
      [FeedSource.Heureka]: t`Heureka`,
      [FeedSource.Zbozi]: t`Zboží.cz`,
      [FeedSource.GoogleMerchant]: t`Google Merchant`,
    },
    feedStatus: {
      [FeedStatus.Pending]: t`Pending`,
      [FeedStatus.Processing]: t`Processing`,
      [FeedStatus.Embedding]: t`Embedding`,
      [FeedStatus.Done]: t`Done`,
      [FeedStatus.Failed]: t`Failed`,
    },
    feedHealth: {
      [FeedHealth.Pending]: t`Pending`,
      [FeedHealth.Processing]: t`Processing`,
      [FeedHealth.Embedding]: t`Embedding`,
      [FeedHealth.Done]: t`Done`,
      [FeedHealth.Degraded]: t`Degraded`,
      [FeedHealth.Stale]: t`Stale`,
      [FeedHealth.Failed]: t`Failed`,
    },
    shopProductStatus: {
      [ShopProductStatus.Pending]: t`Pending`,
      [ShopProductStatus.Embedding]: t`Embedding`,
      [ShopProductStatus.Done]: t`Done`,
      [ShopProductStatus.Failed]: t`Failed`,
    },
    productAvailabilityStatus: {
      [ProductAvailabilityStatus.InStock]: t`In stock`,
      [ProductAvailabilityStatus.OutOfStock]: t`Out of stock`,
      [ProductAvailabilityStatus.OnOrder]: t`On order`,
      [ProductAvailabilityStatus.PreOrder]: t`Pre-order`,
      [ProductAvailabilityStatus.Unknown]: t`Unknown`,
    },
    shopAttributeDataType: {
      [ShopAttributeDataType.Text]: t`Text`,
      [ShopAttributeDataType.Number]: t`Number`,
      [ShopAttributeDataType.Boolean]: t`Yes / no`,
    },
    shopProductAttributeSource: {
      [ShopProductAttributeSource.Feed]: t`From feed`,
      [ShopProductAttributeSource.Model]: t`From model`,
      [ShopProductAttributeSource.Manual]: t`Edited by hand`,
    },
    role: {
      [Role.SuperAdmin]: t`Super admin`,
      [Role.Admin]: t`Admin`,
    },
    roleWOSuperAdmin: {
      [RoleWOSuperAdmin.Admin]: t`Admin`,
    },
    updateFrequency: {
      [UpdateFrequency.Daily]: t`Daily`,
      [UpdateFrequency.Weekly]: t`Weekly`,
      [UpdateFrequency.BiWeekly]: t`Bi-weekly`,
      [UpdateFrequency.Monthly]: t`Monthly`,
      [UpdateFrequency.Never]: t`Never`,
    },
    websitePageStatus: {
      [WebsitePageStatus.New]: t`New`,
      [WebsitePageStatus.Existing]: t`Tracked`,
      [WebsitePageStatus.Removed]: t`Removed`,
      [WebsitePageStatus.Untracked]: t`Untracked`,
    },
    removalBehavior: {
      [RemovalBehavior.Flag]: t`Flag`,
      [RemovalBehavior.Remove]: t`Remove`,
      [RemovalBehavior.Ignore]: t`Ignore`,
    },
    voiceThinkingLevel: {
      minimal: t`Minimal`,
      low: t`Low`,
      medium: t`Medium`,
      high: t`High`,
    },
    voiceStartOfSpeechSensitivity: {
      START_SENSITIVITY_LOW: t`Low`,
      START_SENSITIVITY_HIGH: t`High`,
    },
    voiceEndOfSpeechSensitivity: {
      END_SENSITIVITY_LOW: t`Low`,
      END_SENSITIVITY_HIGH: t`High`,
    },
  } satisfies EnumTranslations;
}
